/**
 * ============================================================================
 * QuickCreateContactNoteModal — Nova anotação a partir do botão "+" do header
 * ----------------------------------------------------------------------------
 * Fluxo em dois passos, sem sair da tela atual:
 *   1) Seleção do contato (reaproveita `ContactSelector`, busca server-side
 *      via RPC `search_contacts_for_selector`);
 *   2) Editor de anotação — EXATAMENTE o mesmo modal usado na tela de Chat
 *      (`ContactNoteEditorDialog`), com texto rico, anexos e timeline.
 *
 * Nenhuma consulta/RPC nova é criada: tudo é reaproveitamento.
 *
 * RELEASE:
 *   2026-08-07 v1.0.0 - Criação.
 * ============================================================================
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StickyNote } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { ContactNoteEditorDialog } from '@/components/tenant/chat/notes/ContactNoteEditorDialog';
import { createLogger } from '@/lib/logger';

const logger = createLogger('QuickCreateContactNote');

interface QuickCreateContactNoteModalProps {
  /** Fecha o fluxo por completo (chamado pelo provider). */
  onClose: () => void;
}

export function QuickCreateContactNoteModal({ onClose }: QuickCreateContactNoteModalProps) {
  // Contato escolhido no passo 1 (null enquanto não selecionado).
  const [contactId, setContactId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------
  // PASSO 2 — Editor de anotação do chat, já vinculado ao contato
  // ---------------------------------------------------------------------
  if (contactId) {
    return (
      <ContactNoteEditorDialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        noteId={null}
        contactId={contactId}
        onSaved={(noteId) => {
          logger.debug('Anotação criada via botão "+"', { noteId, contactId });
          // Mantém listagens/contadores de anotações em sincronia.
          queryClient.invalidateQueries({ queryKey: ['contact-notes', contactId] });
          queryClient.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
          onClose();
        }}
      />
    );
  }

  // ---------------------------------------------------------------------
  // PASSO 1 — Busca/seleção do contato
  // ---------------------------------------------------------------------
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-amber-600" />
            Nova anotação
          </DialogTitle>
          <DialogDescription>
            Pesquise e selecione o contato para o qual deseja registrar a anotação.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <ContactSelector
            value={contactId}
            onSelect={(id) => setContactId(id)}
            placeholder="Pesquisar contato por nome, telefone ou e-mail..."
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
